/**
 * APP_ADDONS_DATA - Bundled add-on definitions.
 *
 * Compiled into the app bundle at build time — NOT served as a public static asset.
 * To update add-on data, edit this file and re-build. Do not re-introduce the public
 * /assets/data/app-addons.json HTTP endpoint (removed to prevent unauthenticated scraping).
 *
 * CREDIT PACKS: every `type: 'credit_pack'` entry is a recurring MONTHLY
 * subscription (see docs/business/tokenization-economy.md § F). `credits` is the
 * grant per month and `priceMonthlyCents` the monthly price. Packs stack
 * `largest_only`: an org holds one pack and changes size rather than adding a
 * second. Keep these in step with CREDIT_PACKS in api/src/shared/credit-types.ts
 * and NINJA_CREDIT_TOPUP_PACKS in tier-access.service.ts.
 *
 * BUNDLING RULE: every compliance program (`type: 'compliance'`) INCLUDES the
 * Professional plan (Tier 2 platform substrate) at no additional charge. The
 * `requires: { minPlanTier: 2 }` prerequisite on these programs is satisfied by
 * the included plan — customers do not buy Professional separately. Quote/bill
 * the compliance program price as platform-inclusive.
 *
 * Update CHANGELOG.md when modifying add-on data.
 */
export const APP_ADDONS_DATA: Record<string, unknown>[] = [
  {
    id: 'addon_dark_web',
    name: 'Dark Web Monitoring',
    type: 'module',
    billing: ['monthly', 'annual'],
    priceMonthlyCents: 19900,
    priceAnnualCents: 199000,
    requires: { minPlanTier: 1 },
    stacking: { mode: 'largest_only' },
    description:
      'Continuous monitoring of dark web sources for credential leaks, brand exposure, and threat intelligence. Passive monitoring included; on-demand lookups consume Ninja Coins.',
    flags: { comingSoon: false, billableNow: true },
    icon: 'eye-off',
    order: 1,
  },
  {
    id: 'addon_pentest',
    name: 'Penetration Testing',
    type: 'service',
    billing: ['annual'],
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    requires: { minPlanTier: 2 },
    stacking: { mode: 'largest_only' },
    description:
      'Scoped penetration testing engagements. Priced by IP count, network range, and testing type. Contact sales for a quote.',
    flags: { comingSoon: false, billableNow: true, contactSales: true },
    icon: 'target',
    order: 2,
  },
  {
    id: 'addon_soc2_type1',
    name: 'SOC 2 Type I Certification Program',
    type: 'compliance',
    attestationType: 'type_1',
    billing: ['annual'],
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    requires: { minPlanTier: 2 },
    incompatibleWith: ['addon_soc2_type2', 'addon_compliance_bundle'],
    stacking: { mode: 'largest_only' },
    description:
      'Guided SOC 2 Type I readiness — a point-in-time assessment of control design. Automated evidence collection, control mapping, auditor-ready exports, and audit liaison. Includes +2,000 Ninja Coins/month during the program. The on-ramp to Type II: your Type I fee credits toward the full program. Contact sales for white-glove scoping.',
    flags: { comingSoon: false, billableNow: true, contactSales: true },
    icon: 'certificate',
    order: 3,
  },
  {
    id: 'addon_soc2_type2',
    name: 'SOC 2 Type II Certification Program',
    type: 'compliance',
    attestationType: 'type_2',
    upgradesFromAddonId: 'addon_soc2_type1',
    billing: ['annual'],
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    requires: { minPlanTier: 2 },
    incompatibleWith: ['addon_soc2_type1', 'addon_compliance_bundle'],
    stacking: { mode: 'largest_only' },
    description:
      'Full SOC 2 Type II program — control design plus operating effectiveness over a 3–6 month observation window. Automated evidence collection, continuous control monitoring, auditor-ready exports, and audit liaison. Includes +2,000 Ninja Coins/month during the program. A prior Type I purchase credits toward this upgrade. Contact sales for white-glove scoping.',
    flags: { comingSoon: false, billableNow: true, contactSales: true },
    icon: 'certificate',
    order: 4,
  },
  {
    id: 'addon_pci',
    name: 'PCI DSS Certification Program',
    type: 'compliance',
    billing: ['annual'],
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    requires: { minPlanTier: 2 },
    stacking: { mode: 'largest_only' },
    description:
      'PCI DSS compliance automation: SAQ management, scoping, quarterly ASV scanning coordination, evidence vault. Includes +2,000 Ninja Coins/month during the program.',
    flags: { comingSoon: false, billableNow: true, contactSales: true },
    icon: 'credit-card',
    order: 5,
  },
  {
    id: 'addon_iso27001',
    name: 'ISO 27001 Certification Program',
    type: 'compliance',
    billing: ['annual'],
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    requires: { minPlanTier: 2 },
    stacking: { mode: 'largest_only' },
    description:
      'ISO 27001 ISMS implementation support, risk register, audit prep, certification tracking. Includes +2,500 Ninja Coins/month during the program.',
    flags: { comingSoon: false, billableNow: true, contactSales: true },
    icon: 'globe',
    order: 6,
  },
  {
    id: 'addon_compliance_bundle',
    name: 'Multi-Compliance Bundle (SOC 2 + PCI + ISO 27001)',
    type: 'compliance_bundle',
    billing: ['annual'],
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    requires: { minPlanTier: 2 },
    incompatibleWith: [
      'addon_soc2_type1',
      'addon_soc2_type2',
      'addon_pci',
      'addon_iso27001',
    ],
    stacking: { mode: 'largest_only' },
    description:
      'Bundled certification programs for organizations pursuing multiple frameworks. Includes +3,500 Ninja Coins/month during active programs.',
    flags: { comingSoon: false, billableNow: true, contactSales: true },
    icon: 'badge-check',
    order: 7,
  },
  {
    id: 'addon_mssp_portal',
    name: 'MSSP Multi-Tenant Portal',
    type: 'module',
    billing: ['annual'],
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    requires: { minPlanTier: 3 },
    stacking: { mode: 'largest_only' },
    description:
      'White-label multi-tenant portal for MSSPs managing multiple client environments. Enterprise-only; tenant bands and credit pool sizing negotiated per deal.',
    flags: { comingSoon: false, billableNow: true, contactSales: true },
    icon: 'building',
    order: 8,
  },
  {
    id: 'cmmc_certification',
    name: 'CMMC 2.0 Certification Program',
    type: 'compliance',
    billing: ['annual'],
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    requires: { minPlanTier: 2 },
    stacking: { mode: 'largest_only' },
    description:
      'CMMC 2.0 Level 2 compliance: 110 NIST SP 800-171 control tracking, SPRS score calculation, PoAM management, System Security Plan (SSP) builder, and C3PAO audit preparation.',
    flags: { comingSoon: false, billableNow: true, contactSales: true },
    icon: 'shield-check',
    order: 9,
  },
  {
    id: 'addon_iso27701',
    name: 'ISO 27701 Privacy Information Management (PIMS)',
    type: 'compliance',
    billing: ['annual'],
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    requires: { minPlanTier: 2 },
    stacking: { mode: 'largest_only' },
    description:
      'ISO 27701 PIMS implementation support: PII controller/processor role mapping, privacy risk treatment, privacy-specific controls, and certification-body liaison. Extends an ISO 27001 ISMS (recommended) or, under the 2025 edition, runs standalone. Includes +2,500 Ninja Coins/month during the program.',
    flags: { comingSoon: false, billableNow: true, contactSales: true },
    icon: 'globe',
    order: 10,
  },
  {
    id: 'addon_ccpa_cpra',
    name: 'CCPA / CPRA Readiness Program',
    type: 'compliance',
    billing: ['annual'],
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    requires: { minPlanTier: 2 },
    stacking: { mode: 'largest_only' },
    description:
      'California privacy (CCPA/CPRA) readiness: data inventory & mapping, consumer-rights/DSAR workflows, privacy notices and opt-out ("Do Not Sell or Share") mechanisms, mandatory risk assessments, and service-provider/DPA review. Includes +2,000 Ninja Coins/month during the program.',
    flags: { comingSoon: false, billableNow: true, contactSales: true },
    icon: 'shield-check',
    order: 11,
  },
  {
    id: 'addon_hipaa',
    name: 'HIPAA Readiness Program',
    type: 'compliance',
    billing: ['annual'],
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    requires: { minPlanTier: 2 },
    stacking: { mode: 'largest_only' },
    description:
      'HIPAA compliance readiness for Covered Entities and Business Associates handling PHI: Security Rule technical/administrative/physical safeguards, risk analysis, Business Associate Agreement (BAA) management, breach-notification procedures, and workforce policy documentation. Includes +2,000 Ninja Coins/month during the program.',
    flags: { comingSoon: false, billableNow: true, contactSales: true },
    icon: 'badge-check',
    order: 12,
  },
  {
    // Entitlement SKU id is the catalog id (gated by Feature.CSF2_COMPLIANCE in
    // tier-access.service.ts). Price set via sales until the pricing model lands.
    id: 'csf2_certification',
    name: 'NIST CSF 2.0 Assessment Program',
    type: 'compliance',
    billing: ['annual'],
    priceMonthlyCents: 0,
    priceAnnualCents: 0,
    requires: { minPlanTier: 2 },
    stacking: { mode: 'largest_only' },
    description:
      'NIST Cybersecurity Framework 2.0 assessment across all six Functions (Govern, Identify, Protect, Detect, Respond, Recover): automated evidence from your cloud and Microsoft 365 scans mapped through NIST SP 800-53 informative references, with a clear split between automated results and outcomes that need attestation.',
    flags: { comingSoon: false, billableNow: true, contactSales: true },
    icon: 'layers',
    order: 13,
  },
  {
    id: 'addon_credits_starter',
    name: 'Starter Credit Pack',
    type: 'credit_pack',
    billing: ['monthly'],
    priceMonthlyCents: 5000,
    priceAnnualCents: 0,
    credits: 50,
    bonusCoins: 0,
    requires: { minPlanTier: 1 },
    stacking: { mode: 'largest_only' },
    description:
      'Monthly credit pack: 50 Ninja Coins every month on top of your plan allotment. Small ongoing overage buffer. Unused pack coins expire at the end of the pack period. Change size or cancel at any billing boundary.',
    flags: { comingSoon: false, billableNow: true },
    icon: 'lightning',
    order: 14,
  },
  {
    id: 'addon_credits_professional',
    name: 'Professional Credit Pack',
    type: 'credit_pack',
    billing: ['monthly'],
    priceMonthlyCents: 15000,
    priceAnnualCents: 0,
    credits: 165,
    bonusCoins: 15,
    requires: { minPlanTier: 1 },
    stacking: { mode: 'largest_only' },
    description:
      'Monthly credit pack: 165 Ninja Coins every month on top of your plan allotment. Sustained quarterly compliance overflow. Unused pack coins expire at the end of the pack period. Change size or cancel at any billing boundary.',
    flags: { comingSoon: false, billableNow: true },
    icon: 'lightning-charge',
    order: 15,
  },
  {
    id: 'addon_credits_compliance',
    name: 'Compliance Bundle Credit Pack',
    type: 'credit_pack',
    billing: ['monthly'],
    priceMonthlyCents: 25000,
    priceAnnualCents: 0,
    credits: 280,
    bonusCoins: 30,
    requires: { minPlanTier: 1 },
    stacking: { mode: 'largest_only' },
    description:
      'Monthly credit pack: 280 Ninja Coins every month on top of your plan allotment. Compliance program customers without a program add-on. Unused pack coins expire at the end of the pack period. Change size or cancel at any billing boundary.',
    flags: { comingSoon: false, billableNow: true },
    icon: 'stack',
    order: 16,
  },
  {
    id: 'addon_credits_power',
    name: 'Power Credit Pack',
    type: 'credit_pack',
    billing: ['monthly'],
    priceMonthlyCents: 50000,
    priceAnnualCents: 0,
    credits: 575,
    bonusCoins: 75,
    requires: { minPlanTier: 1 },
    stacking: { mode: 'largest_only' },
    description:
      'Monthly credit pack: 575 Ninja Coins every month on top of your plan allotment. Active security teams and MSSP operators. Unused pack coins expire at the end of the pack period. Change size or cancel at any billing boundary.',
    flags: { comingSoon: false, billableNow: true },
    icon: 'rocket',
    order: 17,
  },
  {
    id: 'addon_credits_enterprise_reserve',
    name: 'Enterprise Reserve Credit Pack',
    type: 'credit_pack',
    billing: ['monthly'],
    priceMonthlyCents: 120000,
    priceAnnualCents: 0,
    credits: 1440,
    bonusCoins: 240,
    requires: { minPlanTier: 1 },
    stacking: { mode: 'largest_only' },
    description:
      'Monthly credit pack: 1,440 Ninja Coins every month on top of your plan allotment. Large MSSPs and sustained compliance sprints. Unused pack coins expire at the end of the pack period. Change size or cancel at any billing boundary.',
    flags: { comingSoon: false, billableNow: true },
    icon: 'shield-lock',
    order: 18,
  },
];
